import { Component, HostListener, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { AdminAttributeDetail, AdminAttributeRow } from '../../_interfaces/attribute';
import { AttributesService } from '../../_services/attributes.service';
import { AlertService } from '../../_services/alert.service';
import { UserService } from '../../_services/users.service';
import { ConfirmDialogComponent } from '../../../Shared/_components/confirm-dialog/confirm-dialog.component';
import { PaginatorPagesDirective } from '../../../Shared/_directives/paginator-pages.directive';

type Filter = 'ALL' | 'USED' | 'UNUSED';

// Product attributes (Colour, Finish, Size...) and their values: a searchable
// list, a view panel with every value and how many products use it, and links
// to the create / edit form. Deleting is limited to admin roles (server-side
// too) and to attributes no product uses.
@Component({
    selector: 'app-attributes',
    imports: [PaginatorPagesDirective, MainLayoutComponent, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, MatPaginatorModule, FormsModule, CommonModule],
    templateUrl: './attributes.component.html',
    styles: [`
    .at-fade { animation: at-fade .18s ease-out; }
    .at-slide { animation: at-slide .24s cubic-bezier(.2,.8,.2,1); }
    @keyframes at-fade { from { opacity: 0; } }
    @keyframes at-slide { from { transform: translateX(100%); } }
  `]
})
export class AttributesComponent implements OnInit {
  private dialog = inject(MatDialog);
  readonly Math = Math;

  loading = true;
  loadError = false;
  rows: AdminAttributeRow[] = [];
  visible: AdminAttributeRow[] = [];
  // The whole list is loaded once (a few dozen attributes), so paging is done here.
  pageIndex = 0;
  pageSize = 10;

  search = '';
  filter: Filter = 'ALL';

  selectedId: number | null = null;
  detail: AdminAttributeDetail | null = null;
  detailLoading = false;
  valueSearch = '';

  userInfo: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private attributesService: AttributesService,
    private alertService: AlertService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.userInfo = this.userService.isLoggedIn();
    const selected = Number(this.route.snapshot.queryParamMap.get('selected'));
    if (selected > 0) this.selectedId = selected;
    this.load();
  }

  // Marketing can create and edit, but only admins can delete.
  get canDelete(): boolean {
    const roles = String(this.userInfo?.userrole ?? '').split(',').map((r) => r.trim());
    return ['ADMIN', 'SALEADMIN', 'S-ADMIN'].some((r) => roles.includes(r));
  }

  load(): void {
    this.loading = true;
    this.loadError = false;
    this.attributesService.getAdminAttributes().subscribe({
      next: (rows) => {
        this.rows = rows;
        this.applyFilters();
        if (this.selectedId && rows.some((r) => r.attributeId === this.selectedId)) {
          // Land on the page the selected attribute is on (e.g. coming back from the form).
          const at = this.visible.findIndex((r) => r.attributeId === this.selectedId);
          if (at >= 0) this.pageIndex = Math.floor(at / this.pageSize);
          this.loadDetail(this.selectedId);
        } else {
          this.selectedId = null;
          this.detail = null;
        }
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.loadError = true;
        this.alertService.alert(error?.error?.message || 'Could not load attributes', 'error');
      },
    });
  }

  applyFilters(): void {
    const words = this.search.toLowerCase().split(/\s+/).filter(Boolean);
    this.visible = this.rows.filter((r) => {
      if (this.filter === 'USED' && r.productCount === 0) return false;
      if (this.filter === 'UNUSED' && r.productCount > 0) return false;
      if (!words.length) return true;
      const hay = `${r.attributeName} ${r.attributeSlug} ${r.previewValues.join(' ')}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
    this.pageIndex = 0;
  }

  get pageRows(): AdminAttributeRow[] {
    const start = this.pageIndex * this.pageSize;
    return this.visible.slice(start, start + this.pageSize);
  }

  onPage(event: PageEvent): void {
    if (this.pageSize !== event.pageSize) {
      this.pageSize = event.pageSize;
      this.pageIndex = 0;
    } else {
      this.pageIndex = event.pageIndex;
    }
  }

  onSearch(value: string): void {
    this.search = value;
    this.applyFilters();
  }

  setFilter(f: Filter): void {
    this.filter = f;
    this.applyFilters();
  }

  get counts() {
    return {
      total: this.rows.length,
      values: this.rows.reduce((n, r) => n + r.valueCount, 0),
      used: this.rows.filter((r) => r.productCount > 0).length,
      unused: this.rows.filter((r) => r.productCount === 0).length,
    };
  }

  // Usage bar: products using it, against the most-used attribute.
  get maxProducts(): number {
    return Math.max(1, ...this.rows.map((r) => r.productCount));
  }

  usagePct(row: AdminAttributeRow): number {
    return Math.round((row.productCount / this.maxProducts) * 100);
  }

  // A value's share of the products using the attribute (detail panel bars).
  valuePct(count: number): number {
    const max = Math.max(1, ...(this.detail?.values ?? []).map((v) => v.productCount));
    return Math.round((count / max) * 100);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.selectedId) this.closeDetail();
  }

  // ---- selection / view panel ----
  select(row: AdminAttributeRow): void {
    if (this.selectedId === row.attributeId) return;
    this.selectedId = row.attributeId;
    this.loadDetail(row.attributeId);
  }

  private loadDetail(id: number): void {
    this.detailLoading = true;
    this.valueSearch = '';
    this.attributesService.getAdminAttribute(id).subscribe({
      next: (detail) => {
        if (this.selectedId !== id) return;
        this.detail = detail;
        this.detailLoading = false;
      },
      error: (error) => {
        this.detailLoading = false;
        this.alertService.alert(error?.error?.message || 'Could not load the attribute', 'error');
      },
    });
  }

  closeDetail(): void {
    this.selectedId = null;
    this.detail = null;
  }

  get shownValues() {
    const values = this.detail?.values ?? [];
    const q = this.valueSearch.trim().toLowerCase();
    return q ? values.filter((v) => v.attributeValue.toLowerCase().includes(q)) : values;
  }

  // ---- actions ----
  onCreate(): void {
    this.router.navigate(['/attributes/create']);
  }

  onEdit(id: number): void {
    this.router.navigate(['/attributes/edit', id]);
  }

  // Why an attribute can't be deleted (empty when it can).
  deleteBlockedReason(row: { attributeName: string; productCount: number; core: boolean }): string {
    if (row.core) return 'Used by the product forms';
    if (row.productCount > 0) return `${row.productCount} product${row.productCount === 1 ? ' uses' : 's use'} it`;
    return '';
  }

  onDelete(id: number): void {
    const row = this.rows.find((r) => r.attributeId === id);
    if (!row) return;
    const reason = this.deleteBlockedReason(row);
    if (reason) {
      this.alertService.alert(`"${row.attributeName}" can't be deleted: ${reason}`, 'error');
      return;
    }
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete attribute',
        message: `Delete "${row.attributeName}"?`,
        detail: row.valueCount
          ? `Its ${row.valueCount} value${row.valueCount === 1 ? '' : 's'} will be deleted too. No products use it.`
          : 'No products use it.',
        confirmText: 'Delete attribute',
        tone: 'danger',
      },
    });
    ref.afterClosed().subscribe((result) => {
      if (!result?.confirmation) return;
      this.attributesService.deleteAdminAttribute(id).subscribe({
        next: () => {
          this.alertService.alert('Attribute deleted', 'success');
          if (this.selectedId === id) this.closeDetail();
          this.load();
        },
        error: (error) => this.alertService.alert(error?.error?.message || 'Could not delete the attribute', 'error'),
      });
    });
  }
}
